import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Instagram,
  Youtube,
  Sparkles,
  Heart,
  Gem,
  Flower2,
  Cake,
  Building2,
  ChevronDown,
  ArrowUpRight,
  Quote,
  Calendar,
  Palette,
  Users,
  Trophy,
  Plus,
} from "lucide-react";

// Business constants
const BIZ = {
  phone: "+919696555051",
  phoneDisplay: "+91 96965 55051",
  email: "gstarwedding@gmail.com",
  whatsapp: "919696555051",
  instagram: "https://instagram.com/gstarwedding",
  youtube: "https://youtube.com/@gstargorakhpur3854",
  address:
    "Shop No. 1, GDA Tower, Bilandpur, Gorakhpur, Uttar Pradesh 273001, India",
  plusCode: "Q93F+74 Gorakhpur, Uttar Pradesh",
};

const waLink = (msg = "Hello G STAR! I'd like to enquire about an event.") =>
  `https://wa.me/${BIZ.whatsapp}?text=${encodeURIComponent(msg)}`;

// ---------- NAVBAR ----------
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    ["Services", "#services"],
    ["Events", "#events"],
    ["Portfolio", "#portfolio"],
    ["Why Us", "#why"],
    ["Process", "#process"],
    ["Contact", "#contact"],
  ];
  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0a1220]/85 backdrop-blur-xl border-b border-[#d4af37]/15 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a
          href="#top"
          data-testid="navbar-logo"
          className="flex items-center gap-2 group"
        >
          <Sparkles className="w-5 h-5 text-[#d4af37] group-hover:rotate-12 transition-transform" />
          <span className="font-display text-2xl md:text-3xl gold-text tracking-wide">
            G&nbsp;STAR
          </span>
          <span className="hidden md:inline text-[10px] font-mono-d text-[#d4af37]/60 self-end mb-1">
            EVENTS · GORAKHPUR
          </span>
        </a>

        <ul className="hidden lg:flex items-center gap-9 font-body text-sm text-[#f5ecd9]/85">
          {links.map(([label, href]) => (
            <li key={href}>
              <a
                href={href}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className="gold-underline hover:text-[#d4af37] transition-colors"
              >
                {label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${BIZ.phone}`}
            data-testid="navbar-call"
            className="btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-2"
          >
            <Phone className="w-3.5 h-3.5" /> Call
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="navbar-book"
            className="btn-gold rounded-full px-5 py-2.5 text-xs flex items-center gap-2"
          >
            Book Consultation <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>

        <button
          data-testid="navbar-toggle"
          className="lg:hidden text-[#d4af37] p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          <div className="space-y-1.5">
            <span
              className={`block h-px w-6 bg-[#d4af37] transition-transform ${
                open ? "rotate-45 translate-y-2" : ""
              }`}
            />
            <span
              className={`block h-px w-6 bg-[#d4af37] transition-opacity ${
                open ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block h-px w-6 bg-[#d4af37] transition-transform ${
                open ? "-rotate-45 -translate-y-2" : ""
              }`}
            />
          </div>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-[#0a1220]/95 backdrop-blur-xl border-t border-[#d4af37]/15"
          >
            <ul className="flex flex-col py-4 px-6 gap-4 font-body text-[#f5ecd9]/90">
              {links.map(([label, href]) => (
                <li key={href}>
                  <a
                    href={href}
                    onClick={() => setOpen(false)}
                    className="block py-1"
                  >
                    {label}
                  </a>
                </li>
              ))}
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                className="btn-gold rounded-full px-5 py-3 text-xs text-center mt-2"
              >
                Book Consultation on WhatsApp
              </a>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// ---------- HERO ----------
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative min-h-screen flex items-center overflow-hidden grain"
    >
      <div className="orb bg-[#d4af37]/40 w-[420px] h-[420px] -top-20 -left-20" />
      <div className="orb bg-[#8a3a8a]/30 w-[520px] h-[520px] top-40 right-[-100px]" />
      <div className="orb bg-[#1a3a6b]/40 w-[600px] h-[600px] bottom-[-200px] left-1/3" />

      <motion.div
        style={{ y, opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 grid lg:grid-cols-12 gap-10 items-center w-full"
      >
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full glass"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] pulse-gold" />
            <span className="font-mono-d text-[11px] text-[#d4af37]">
              EST · GORAKHPUR · 250+ EVENTS DELIVERED
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="font-display text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] text-[#f5ecd9]"
          >
            Crafting <em className="gold-text not-italic">extraordinary</em>
            <br />
            events &amp; <span className="italic font-light">timeless</span>{" "}
            memories.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-8 max-w-xl font-body text-base md:text-lg text-[#f5ecd9]/70 leading-relaxed"
          >
            From regal mandaps to intimate anniversaries, G STAR designs
            celebrations across Gorakhpur with cinematic décor, flawless
            coordination, and a personal touch you can feel in every detail.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink("Hi G STAR! I'd like to book a free consultation.")}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-book"
              className="btn-gold rounded-full px-7 py-4 inline-flex items-center gap-2 text-sm"
            >
              <MessageCircle className="w-4 h-4" />
              Book Free Consultation
            </a>
            <a
              href="#portfolio"
              data-testid="hero-portfolio"
              className="btn-ghost rounded-full px-7 py-4 inline-flex items-center gap-2 text-sm"
            >
              View Portfolio <ArrowUpRight className="w-4 h-4" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-lg"
          >
            {[
              ["10+", "Years of Craft"],
              ["250+", "Events Curated"],
              ["100%", "Client Smiles"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-4xl gold-text">{n}</div>
                <div className="text-xs font-mono-d text-[#f5ecd9]/50 mt-1">
                  {l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.3 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#d4af37]/30 glass">
            <img
              src="/images/wedding-mandap-1.jpg"
              alt="G STAR signature wedding mandap"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1220] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="font-mono-d text-[10px] text-[#d4af37] mb-2">
                SIGNATURE · 2026
              </div>
              <div className="font-display text-2xl text-[#f5ecd9]">
                The Royal Mandap Series
              </div>
            </div>
          </div>
          <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-2xl overflow-hidden border border-[#d4af37]/30 hidden md:block">
            <img
              src="/images/decoration-floral-1.jpg"
              alt="floral detail"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#d4af37]/70">
        <span className="text-[10px] font-mono-d tracking-widest">
          SCROLL
        </span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </div>
    </section>
  );
};

// ---------- MARQUEE ----------
const Marquee = () => {
  const items = [
    "Weddings",
    "Engagements",
    "Birthdays",
    "Anniversaries",
    "Corporate Galas",
    "Mehndi & Sangeet",
    "Receptions",
    "Pre-Wedding",
  ];
  const seq = [...items, ...items];
  return (
    <div
      data-testid="marquee"
      className="relative border-y border-[#d4af37]/15 bg-[#0a1220]/60 overflow-hidden py-6"
    >
      <div className="flex marquee-track gap-12 whitespace-nowrap w-max">
        {seq.map((t, i) => (
          <span
            key={i}
            className="font-display text-3xl md:text-5xl italic text-[#f5ecd9]/30 flex items-center gap-12"
          >
            {t}
            <Sparkles className="w-5 h-5 text-[#d4af37]" />
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------- SECTION HEADER ----------
const SectionHeader = ({ kicker, title, sub, align = "left" }) => (
  <div
    className={`max-w-3xl mb-14 ${
      align === "center" ? "mx-auto text-center" : ""
    }`}
  >
    <div className="font-mono-d text-[11px] text-[#d4af37] mb-4 tracking-widest">
      {kicker}
    </div>
    <h2 className="font-display text-4xl md:text-6xl text-[#f5ecd9] leading-[1.05]">
      {title}
    </h2>
    {sub && (
      <p className="mt-5 font-body text-[#f5ecd9]/65 text-base md:text-lg leading-relaxed">
        {sub}
      </p>
    )}
  </div>
);

// ---------- SERVICES ----------
const Services = () => {
  const items = [
    {
      icon: Heart,
      title: "Weddings",
      desc: "Multi-day weddings with bespoke mandaps, floral artistry, lighting design and choreographed flow.",
    },
    {
      icon: Gem,
      title: "Engagements",
      desc: "Intimate ring ceremonies styled with elegant stages, mood lighting and refined floral palettes.",
    },
    {
      icon: Flower2,
      title: "Décor & Florals",
      desc: "Theme-led décor — from regal traditional to clean contemporary — with premium florals.",
    },
    {
      icon: Cake,
      title: "Birthdays",
      desc: "Whimsical kid parties to glam milestone celebrations, with custom backdrops & entertainment.",
    },
    {
      icon: Sparkles,
      title: "Anniversaries",
      desc: "Romantic, candlelit set-ups designed to celebrate the years you've built together.",
    },
    {
      icon: Building2,
      title: "Corporate",
      desc: "Brand launches, conferences and award nights with disciplined production & polished AV.",
    },
  ];
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-28 md:py-36 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          kicker="01 / WHAT WE DO"
          title={
            <>
              Six services. One{" "}
              <em className="gold-text not-italic">obsession</em>: making your
              day unforgettable.
            </>
          }
          sub="Every G STAR celebration is hand-built around your story, your guests and your budget — no templates, no shortcuts."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.07, duration: 0.6 }}
              data-testid={`service-card-${s.title.toLowerCase()}`}
              className="glass rounded-2xl p-7 hover-lift group"
            >
              <div className="flex items-start justify-between mb-7">
                <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center group-hover:bg-[#d4af37] group-hover:text-[#0f1b2e] transition-colors">
                  <s.icon className="w-5 h-5 text-[#d4af37] group-hover:text-[#0f1b2e] transition-colors" />
                </div>
                <span className="font-mono-d text-[10px] text-[#f5ecd9]/30">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-display text-2xl text-[#f5ecd9] mb-3">
                {s.title}
              </h3>
              <p className="font-body text-sm text-[#f5ecd9]/60 leading-relaxed">
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- EVENTS GALLERY ----------
const Events = () => {
  const cards = [
    {
      img: "/images/wedding-mandap-1.jpg",
      icon: "👰",
      title: "Weddings",
      tag: "Sacred · Cinematic",
    },
    {
      img: "/images/engagement-setup-1.jpg",
      icon: "💍",
      title: "Engagements",
      tag: "Elegant · Intimate",
    },
    {
      img: "/images/decoration-floral-1.jpg",
      icon: "🌸",
      title: "Décor & Florals",
      tag: "Floral · Bespoke",
    },
    {
      img: "/images/birthday-decor-1.jpeg",
      icon: "🎂",
      title: "Birthdays",
      tag: "Joyful · Personal",
    },
    {
      img: "/images/event-lounge-1.jpeg",
      icon: "💑",
      title: "Anniversaries",
      tag: "Romantic · Warm",
    },
    {
      img: "/images/wedding-stage-1.jpg",
      icon: "🏢",
      title: "Corporate",
      tag: "Polished · Branded",
    },
  ];
  return (
    <section
      id="events"
      data-testid="events-section"
      className="relative py-28 md:py-36 px-6 bg-gradient-to-b from-transparent via-[#0f1b2e]/40 to-transparent"
    >
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          kicker="02 / EVENT GALLERY"
          title={
            <>
              Every occasion, <em className="gold-text not-italic">staged</em>{" "}
              to perfection.
            </>
          }
          sub="A glimpse of celebrations we've designed across Gorakhpur — each one shaped by the people at its centre."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              data-testid={`event-card-${c.title.toLowerCase().replace(/\s|&/g, "-")}`}
              className="event-card relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#d4af37]/20 group cursor-pointer"
            >
              <img
                src={c.img}
                alt={c.title}
                className="event-img absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1220] via-[#0a1220]/40 to-[#0a1220]/20" />
              <div className="absolute inset-0 flex flex-col justify-between p-7">
                <div className="flex items-start justify-between">
                  <span className="text-4xl drop-shadow-2xl">{c.icon}</span>
                  <span className="font-mono-d text-[10px] text-[#d4af37] bg-[#0a1220]/60 backdrop-blur-md px-3 py-1.5 rounded-full border border-[#d4af37]/30">
                    {c.tag}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-3xl text-[#f5ecd9] mb-2">
                    {c.title}
                  </h3>
                  <a
                    href={waLink(`Hi! I'd like to enquire about ${c.title}.`)}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono-d text-[11px] text-[#d4af37] inline-flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  >
                    ENQUIRE NOW <ArrowUpRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- PORTFOLIO ----------
const Portfolio = () => {
  const items = [
    {
      img: "/images/event-lounge-1.jpeg",
      title: "Luxury Lounge Setup",
      desc: "Premium wedding guest area with statement seating.",
      span: "wide",
    },
    {
      img: "/images/wedding-mandap-1.jpg",
      title: "Wedding Mandap",
      desc: "Gold-and-floral sacred centrepiece.",
      span: "tall",
    },
    {
      img: "/images/engagement-setup-1.jpg",
      title: "Engagement Ceremony",
      desc: "Pastel palette with refined lighting.",
      span: "wide",
    },
    {
      img: "/images/decoration-floral-1.jpg",
      title: "Floral Arch",
      desc: "Layered fresh floral installation.",
      span: "normal",
    },
    {
      img: "/images/wedding-stage-1.jpg",
      title: "Gold Stage",
      desc: "Dramatic drapes & accent florals.",
      span: "normal",
    },
    {
      img: "/images/wedding-lounge-1.jpg",
      title: "Premium Couple Lounge",
      desc: "Artistic backdrop for the bride & groom.",
      span: "wide",
    },
    {
      img: "/images/wedding-arch-1.jpg",
      title: "Floral Wedding Arch",
      desc: "Romantic red & white blooms.",
      span: "wide",
    },
    {
      img: "/images/event-decor-1.jpeg",
      title: "Event Décor Styling",
      desc: "Layered textures, lighting & florals.",
      span: "normal",
    },
    {
      img: "/images/lounge-setup-1.jpeg",
      title: "Lounge Setup",
      desc: "High-end seating arrangement.",
      span: "normal",
    },
  ];
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-28 md:py-36 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <SectionHeader
            kicker="03 / PORTFOLIO"
            title={
              <>
                A curated <em className="gold-text not-italic">canvas</em> of
                our work.
              </>
            }
            sub="Selected moments from real weddings, lounges and ceremonies — hover to read the story."
          />
          <a
            href={BIZ.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="portfolio-instagram"
            className="btn-ghost rounded-full px-5 py-3 text-xs inline-flex items-center gap-2 mb-14"
          >
            <Instagram className="w-3.5 h-3.5" /> See more on Instagram
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 auto-rows-[220px] md:auto-rows-[260px]">
          {items.map((p, i) => {
            const spanClass =
              p.span === "wide"
                ? "lg:col-span-2"
                : p.span === "tall"
                ? "lg:row-span-2"
                : "";
            return (
              <motion.div
                key={p.title + i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: (i % 4) * 0.08, duration: 0.6 }}
                data-testid={`portfolio-item-${i}`}
                className={`portfolio-card relative rounded-2xl overflow-hidden border border-[#d4af37]/15 cursor-pointer ${spanClass}`}
              >
                <img
                  src={p.img}
                  alt={p.title}
                  className="pimg absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="overlay absolute inset-0 flex flex-col justify-end p-6">
                  <div className="font-mono-d text-[10px] text-[#0f1b2e] mb-2 opacity-80">
                    G STAR · 0{i + 1}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-[#0f1b2e] mb-2 leading-tight">
                    {p.title}
                  </h3>
                  <p className="font-body text-sm text-[#0f1b2e]/85 max-w-xs">
                    {p.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------- WHY US ----------
const WhyUs = () => {
  const items = [
    {
      icon: Palette,
      title: "Bespoke Design",
      desc: "No two G STAR events look alike. Every mandap, lounge and aisle is sketched for you.",
    },
    {
      icon: Users,
      title: "One Team, Start to Finish",
      desc: "A dedicated planner stays with you from first call to final farewell — no handoffs.",
    },
    {
      icon: Trophy,
      title: "Premium Vendors Only",
      desc: "Florists, AV, lighting and caterers we've personally vetted over a decade in Gorakhpur.",
    },
    {
      icon: Calendar,
      title: "Calm on the Big Day",
      desc: "Cinematic execution backed by run-sheets, rehearsals and an on-site command crew.",
    },
  ];
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-28 md:py-36 px-6 bg-gradient-to-b from-transparent via-[#0f1b2e]/40 to-transparent"
    >
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          kicker="04 / WHY G STAR"
          title={
            <>
              The little things that make a{" "}
              <em className="gold-text not-italic">big</em> difference.
            </>
          }
        />
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((w, i) => (
            <motion.div
              key={w.title}
              initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              data-testid={`why-card-${i}`}
              className="glass rounded-2xl p-8 flex gap-6 hover-lift"
            >
              <div className="w-14 h-14 shrink-0 rounded-2xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <w.icon className="w-6 h-6 text-[#d4af37]" />
              </div>
              <div>
                <h3 className="font-display text-2xl text-[#f5ecd9] mb-2">
                  {w.title}
                </h3>
                <p className="font-body text-sm text-[#f5ecd9]/65 leading-relaxed">
                  {w.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- PROCESS ----------
const Process = () => {
  const steps = [
    ["Discovery Call", "We listen — to your story, your guests, your dream."],
    ["Concept & Moodboard", "Visual direction, palette, layout and budget framework."],
    ["Design Blueprint", "Detailed mandap, stage and floor plans signed off."],
    ["Production Build", "Décor, florals, lighting and AV crafted in our studio."],
    ["On-site Setup", "Pre-event installation and a full dress rehearsal."],
    ["The Big Day", "Our crew runs the show — you simply enjoy it."],
  ];
  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative py-28 md:py-36 px-6"
    >
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          kicker="05 / OUR PROCESS"
          title={
            <>
              Six steps from{" "}
              <em className="gold-text not-italic">hello</em> to happily ever
              after.
            </>
          }
        />
        <div className="relative">
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#d4af37]/40 to-transparent" />
          <div className="space-y-12 md:space-y-20">
            {steps.map(([title, desc], i) => {
              const isLeft = i % 2 === 0;
              return (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6 }}
                  data-testid={`process-step-${i}`}
                  className={`relative md:grid md:grid-cols-2 md:gap-12 items-center ${
                    isLeft ? "" : "md:[&>*:first-child]:order-2"
                  }`}
                >
                  <div className="pl-16 md:pl-0 md:pr-12 md:text-right">
                    {!isLeft && <div className="md:hidden" />}
                    <div
                      className={`${
                        isLeft ? "md:text-right" : "md:text-left"
                      }`}
                    >
                      <span className="num-badge text-6xl md:text-7xl gold-text block leading-none mb-3">
                        0{i + 1}
                      </span>
                      <h3 className="font-display text-3xl text-[#f5ecd9] mb-2">
                        {title}
                      </h3>
                      <p className="font-body text-[#f5ecd9]/60 max-w-md md:ml-auto">
                        {desc}
                      </p>
                    </div>
                  </div>
                  <div className="hidden md:block" />
                  <div className="absolute left-6 md:left-1/2 -translate-x-1/2 top-4 w-3 h-3 rounded-full bg-[#d4af37] pulse-gold" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- TESTIMONIALS ----------
const Testimonials = () => {
  const quotes = [
    {
      name: "Anjali & Rohit",
      role: "Wedding · Dec 2025",
      text: "The mandap took our breath away. G STAR turned every Pinterest dream we had into something even more beautiful.",
    },
    {
      name: "Priya Shukla",
      role: "Engagement · 2025",
      text: "Calm, creative and ridiculously detail-oriented. They handled everything so I could actually enjoy my own engagement.",
    },
    {
      name: "Kunal Verma",
      role: "Corporate Gala · 2025",
      text: "Polished production, on-time, on-brand. Our launch event in Gorakhpur looked like a metro-city show.",
    },
  ];
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-28 md:py-36 px-6 bg-gradient-to-b from-transparent via-[#0f1b2e]/40 to-transparent"
    >
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          kicker="06 / KIND WORDS"
          title={
            <>
              Loved by the couples and brands we{" "}
              <em className="gold-text not-italic">serve</em>.
            </>
          }
        />
        <div className="grid md:grid-cols-3 gap-5">
          {quotes.map((q, i) => (
            <motion.div
              key={q.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              data-testid={`testimonial-${i}`}
              className="glass rounded-2xl p-8"
            >
              <Quote className="w-7 h-7 text-[#d4af37] mb-5" />
              <p className="font-display text-xl text-[#f5ecd9] leading-snug mb-6 italic">
                &ldquo;{q.text}&rdquo;
              </p>
              <div className="thin-line mb-4" />
              <div className="font-body text-sm text-[#f5ecd9]">{q.name}</div>
              <div className="font-mono-d text-[10px] text-[#d4af37]/80 mt-1">
                {q.role.toUpperCase()}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- FAQ ----------
const FAQ = () => {
  const faqs = [
    [
      "How far in advance should we book G STAR?",
      "For weddings, 3–6 months ahead is ideal. For smaller events, 4–6 weeks works comfortably. We do accept select last-minute bookings — message us on WhatsApp.",
    ],
    [
      "Do you handle events outside Gorakhpur?",
      "Yes. While we're proudly based in Gorakhpur, we frequently design events across Uttar Pradesh and destination weddings within India.",
    ],
    [
      "Can you work within my budget?",
      "Absolutely. We design at every tier — from intimate gatherings to grand multi-day weddings — and quote transparently after a discovery call.",
    ],
    [
      "Do you provide photography & catering?",
      "We coordinate trusted vendors for photography, catering, makeup and entertainment as part of a full-service package.",
    ],
  ];
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-28 md:py-36 px-6"
    >
      <div className="max-w-4xl mx-auto">
        <SectionHeader
          kicker="07 / QUESTIONS"
          title={
            <>
              Things people often <em className="gold-text not-italic">ask</em>.
            </>
          }
        />
        <div className="space-y-3">
          {faqs.map(([q, a], i) => (
            <div
              key={q}
              data-testid={`faq-${i}`}
              className="glass rounded-2xl overflow-hidden"
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between text-left p-6 group"
              >
                <span className="font-display text-xl text-[#f5ecd9] pr-6">
                  {q}
                </span>
                <Plus
                  className={`w-5 h-5 text-[#d4af37] shrink-0 transition-transform duration-400 ${
                    open === i ? "rotate-45" : ""
                  }`}
                />
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 font-body text-[#f5ecd9]/65 leading-relaxed">
                      {a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- CONTACT ----------
const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    eventType: "Wedding",
    date: "",
    message: "",
  });
  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    const text =
      `Hi G STAR! 👋%0A%0A` +
      `*Name:* ${form.name}%0A` +
      `*Phone:* ${form.phone}%0A` +
      `*Event:* ${form.eventType}%0A` +
      `*Preferred Date:* ${form.date || "Flexible"}%0A` +
      `*Message:* ${form.message}`;
    window.open(`https://wa.me/${BIZ.whatsapp}?text=${text}`, "_blank");
  };
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-36 px-6 bg-gradient-to-b from-transparent via-[#0f1b2e]/40 to-transparent"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="font-mono-d text-[11px] text-[#d4af37] mb-4 tracking-widest">
            08 / GET IN TOUCH
          </div>
          <h2 className="font-display text-4xl md:text-6xl text-[#f5ecd9] leading-[1.05] mb-6">
            Let&rsquo;s design your <em className="gold-text not-italic">moment</em>.
          </h2>
          <p className="font-body text-[#f5ecd9]/65 mb-10 leading-relaxed">
            Tell us a little about your celebration — we&rsquo;ll reply on WhatsApp
            within a few hours with ideas and next steps.
          </p>

          <div className="space-y-4">
            <a
              href={`tel:${BIZ.phone}`}
              data-testid="contact-call"
              className="glass rounded-2xl p-5 flex items-center gap-4 hover-lift group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <Phone className="w-5 h-5 text-[#d4af37]" />
              </div>
              <div>
                <div className="font-mono-d text-[10px] text-[#d4af37]/80">
                  CALL NOW
                </div>
                <div className="font-display text-xl text-[#f5ecd9]">
                  {BIZ.phoneDisplay}
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 ml-auto text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="glass rounded-2xl p-5 flex items-center gap-4 hover-lift group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-[#d4af37]" />
              </div>
              <div>
                <div className="font-mono-d text-[10px] text-[#d4af37]/80">
                  WHATSAPP US
                </div>
                <div className="font-display text-xl text-[#f5ecd9]">
                  Chat instantly
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 ml-auto text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <a
              href={`mailto:${BIZ.email}`}
              data-testid="contact-email"
              className="glass rounded-2xl p-5 flex items-center gap-4 hover-lift group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#d4af37]" />
              </div>
              <div>
                <div className="font-mono-d text-[10px] text-[#d4af37]/80">
                  EMAIL
                </div>
                <div className="font-display text-xl text-[#f5ecd9]">
                  {BIZ.email}
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 ml-auto text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <div className="glass rounded-2xl p-5 flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-[#d4af37]" />
              </div>
              <div>
                <div className="font-mono-d text-[10px] text-[#d4af37]/80">
                  STUDIO
                </div>
                <div className="font-body text-sm text-[#f5ecd9]/85 leading-relaxed">
                  {BIZ.address}
                </div>
                <div className="font-mono-d text-[10px] text-[#d4af37]/60 mt-1">
                  PLUS CODE · {BIZ.plusCode}
                </div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 glass rounded-3xl p-8 md:p-10"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="font-mono-d text-[10px] text-[#d4af37]/80 block mb-2">
                YOUR NAME
              </label>
              <input
                required
                name="name"
                value={form.name}
                onChange={onChange}
                data-testid="form-name"
                placeholder="Anjali Sharma"
                className="gs-input w-full rounded-xl px-4 py-3.5 font-body"
              />
            </div>
            <div>
              <label className="font-mono-d text-[10px] text-[#d4af37]/80 block mb-2">
                PHONE
              </label>
              <input
                required
                name="phone"
                value={form.phone}
                onChange={onChange}
                data-testid="form-phone"
                placeholder="+91 ..."
                className="gs-input w-full rounded-xl px-4 py-3.5 font-body"
              />
            </div>
            <div>
              <label className="font-mono-d text-[10px] text-[#d4af37]/80 block mb-2">
                EVENT TYPE
              </label>
              <select
                name="eventType"
                value={form.eventType}
                onChange={onChange}
                data-testid="form-event-type"
                className="gs-input w-full rounded-xl px-4 py-3.5 font-body"
              >
                {[
                  "Wedding",
                  "Engagement",
                  "Birthday",
                  "Anniversary",
                  "Corporate",
                  "Décor Only",
                  "Other",
                ].map((o) => (
                  <option key={o} className="bg-[#0a1220]">
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-mono-d text-[10px] text-[#d4af37]/80 block mb-2">
                PREFERRED DATE
              </label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={onChange}
                data-testid="form-date"
                className="gs-input w-full rounded-xl px-4 py-3.5 font-body"
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="font-mono-d text-[10px] text-[#d4af37]/80 block mb-2">
              TELL US ABOUT YOUR EVENT
            </label>
            <textarea
              required
              name="message"
              value={form.message}
              onChange={onChange}
              data-testid="form-message"
              rows={5}
              placeholder="Number of guests, venue, theme ideas, anything that matters to you..."
              className="gs-input w-full rounded-xl px-4 py-3.5 font-body resize-none"
            />
          </div>
          <button
            type="submit"
            data-testid="form-submit"
            className="btn-gold rounded-full px-7 py-4 mt-7 inline-flex items-center gap-2 text-sm"
          >
            <MessageCircle className="w-4 h-4" />
            Send via WhatsApp
          </button>
          <p className="font-mono-d text-[10px] text-[#f5ecd9]/40 mt-4">
            BY SENDING THIS FORM, A WHATSAPP CHAT OPENS WITH YOUR DETAILS
            PRE-FILLED.
          </p>

          <div className="mt-10 rounded-2xl overflow-hidden border border-[#d4af37]/20">
            <iframe
              title="G STAR Studio Map"
              src="https://www.google.com/maps?q=Shop+No.+1,+GDA+Tower,+Bilandpur,+Gorakhpur,+Uttar+Pradesh+273001&output=embed"
              className="w-full h-64 map-frame"
              loading="lazy"
              data-testid="map-iframe"
            />
          </div>
        </form>
      </div>
    </section>
  );
};

// ---------- FOOTER ----------
const Footer = () => (
  <footer
    data-testid="footer"
    className="relative pt-20 pb-10 px-6 border-t border-[#d4af37]/15"
  >
    <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <a href="#top" className="flex items-center gap-2 mb-5">
          <Sparkles className="w-5 h-5 text-[#d4af37]" />
          <span className="font-display text-3xl gold-text">G STAR</span>
        </a>
        <p className="font-body text-[#f5ecd9]/60 max-w-sm leading-relaxed">
          Premium event design & planning from the heart of Gorakhpur. Built on
          a decade of craft, calm and care.
        </p>
        <div className="flex gap-3 mt-6">
          <a
            href={BIZ.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-instagram"
            className="w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-[#d4af37]/15 transition"
          >
            <Instagram className="w-4 h-4 text-[#d4af37]" />
          </a>
          <a
            href={BIZ.youtube}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-youtube"
            className="w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-[#d4af37]/15 transition"
          >
            <Youtube className="w-4 h-4 text-[#d4af37]" />
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-whatsapp"
            className="w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-[#d4af37]/15 transition"
          >
            <MessageCircle className="w-4 h-4 text-[#d4af37]" />
          </a>
        </div>
      </div>

      <div className="md:col-span-3">
        <div className="font-mono-d text-[10px] text-[#d4af37] mb-5 tracking-widest">
          EXPLORE
        </div>
        <ul className="space-y-3 font-body text-sm text-[#f5ecd9]/70">
          <li>
            <a href="#services" className="gold-underline">
              Services
            </a>
          </li>
          <li>
            <a href="#portfolio" className="gold-underline">
              Portfolio
            </a>
          </li>
          <li>
            <a href="#process" className="gold-underline">
              Our Process
            </a>
          </li>
          <li>
            <a href="#faq" className="gold-underline">
              FAQ
            </a>
          </li>
        </ul>
      </div>

      <div className="md:col-span-4">
        <div className="font-mono-d text-[10px] text-[#d4af37] mb-5 tracking-widest">
          REACH US
        </div>
        <ul className="space-y-3 font-body text-sm text-[#f5ecd9]/70">
          <li className="flex gap-3">
            <Phone className="w-4 h-4 text-[#d4af37] shrink-0 mt-0.5" />
            <a href={`tel:${BIZ.phone}`}>{BIZ.phoneDisplay}</a>
          </li>
          <li className="flex gap-3">
            <Mail className="w-4 h-4 text-[#d4af37] shrink-0 mt-0.5" />
            <a href={`mailto:${BIZ.email}`}>{BIZ.email}</a>
          </li>
          <li className="flex gap-3">
            <MapPin className="w-4 h-4 text-[#d4af37] shrink-0 mt-0.5" />
            <span>{BIZ.address}</span>
          </li>
        </ul>
      </div>
    </div>

    <div className="max-w-7xl mx-auto mt-14 pt-6 border-t border-[#d4af37]/10 flex flex-wrap justify-between items-center gap-3 text-xs font-mono-d text-[#f5ecd9]/40">
      <span>© {new Date().getFullYear()} G STAR EVENTS · GORAKHPUR</span>
      <a
        href="https://www.deepflxadwrld.in"
        target="_blank"
        rel="noreferrer"
        className="hover:text-[#d4af37] transition"
      >
        Powered by Deepflx Adwrld
      </a>
    </div>
  </footer>
);

// ---------- STICKY MOBILE CTAs ----------
const StickyCTA = () => (
  <div
    data-testid="sticky-cta"
    className="md:hidden fixed bottom-4 left-4 right-4 z-40 flex gap-3 sticky-cta"
  >
    <a
      href={`tel:${BIZ.phone}`}
      data-testid="sticky-call"
      className="flex-1 btn-ghost bg-[#0a1220]/80 backdrop-blur-md rounded-full py-3.5 flex items-center justify-center gap-2 text-xs font-semibold"
    >
      <Phone className="w-4 h-4" /> Call
    </a>
    <a
      href={waLink()}
      target="_blank"
      rel="noreferrer"
      data-testid="sticky-whatsapp"
      className="flex-1 btn-gold rounded-full py-3.5 flex items-center justify-center gap-2 text-xs"
    >
      <MessageCircle className="w-4 h-4" /> WhatsApp
    </a>
  </div>
);

// ---------- FLOATING WHATSAPP ----------
const FloatingWA = () => (
  <a
    href={waLink()}
    target="_blank"
    rel="noreferrer"
    data-testid="floating-whatsapp"
    aria-label="Chat on WhatsApp"
    className="hidden md:flex fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full btn-gold items-center justify-center shadow-2xl"
  >
    <MessageCircle className="w-6 h-6" />
  </a>
);

// ---------- MAIN PAGE ----------
const GStarEvents = () => {
  useEffect(() => {
    document.title = "G STAR Events · Premium Event Planners in Gorakhpur";
  }, []);
  return (
    <div className="bg-[#0a1220] text-[#f5ecd9] min-h-screen relative">
      <Navbar />
      <Hero />
      <Marquee />
      <Services />
      <Events />
      <Portfolio />
      <WhyUs />
      <Process />
      <Testimonials />
      <FAQ />
      <Contact />
      <Footer />
      <StickyCTA />
      <FloatingWA />
    </div>
  );
};

export default GStarEvents;
